import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { ConversationDetails } from "@/types/conversation/conversation";
import { formatDuration } from "@/utils/formatting/general";
import { buildAttentionQueue, formatRelativeDate, type AttentionCall } from "../utils/aggregates";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface InsightsActionQueueProps {
  conversations: ConversationDetails[];
}

const REASON_STYLES: Record<AttentionCall["reasonKind"], string> = {
  negative: "bg-[#FF4DB8]/10 text-[#FF4DB8] border-[#FF4DB8]/30",
  escalation: "bg-[#FFBB33]/15 text-[#B8860B] border-[#FFBB33]/40",
  "no-analysis": "bg-gray-100 text-gray-600 border-gray-200",
  "no-lead": "bg-[#4E9CFF]/10 text-[#4E9CFF] border-[#4E9CFF]/30",
};

const REASON_LABEL: Record<AttentionCall["reasonKind"], string> = {
  negative: "Negative",
  escalation: "Escalation",
  "no-analysis": "Run analysis",
  "no-lead": "No lead",
};

export const InsightsActionQueue = ({ conversations }: InsightsActionQueueProps) => {
  const queue = buildAttentionQueue(conversations);

  return (
    <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-sm mb-6">
      <div className="rounded-[14px] bg-white overflow-hidden">
        <div
          className="px-5 py-4 border-b border-[#4E9CFF]/10"
          style={{
            background:
              "linear-gradient(135deg, rgba(78,156,255,0.06) 0%, rgba(255,77,184,0.06) 100%)",
          }}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <GenieIcon
                name="clipboard-check"
                size={26}
                color="#4E9CFF"
                secondaryColor="#FF4DB8"
                gradientEnabled
                label="Action queue"
              />
              <div>
                <h3 className="text-base font-bold text-gray-900">Action queue</h3>
                <p className="text-xs text-gray-600 mt-0.5">
                  Conversations worth a closer look — sorted by what needs your attention first
                </p>
              </div>
            </div>
            <span className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-[#4E9CFF]/20 text-xs font-semibold text-gray-700">
              {queue.length} flagged
            </span>
          </div>
        </div>

        <div className="px-5 py-4">
          {queue.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-sm text-gray-600 font-medium">All clear here.</p>
              <p className="text-xs text-gray-500 mt-1">
                Nothing's flagged for review across these conversations.
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {queue.map((item, idx) => {
                const linkTarget = item.fullPageId
                  ? `/genie-hub/conversations/${item.fullPageId}`
                  : null;
                const Row = (
                  <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border flex-shrink-0 ${REASON_STYLES[item.reasonKind]}`}
                    >
                      {REASON_LABEL[item.reasonKind]}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 truncate">
                        <span className="font-semibold">{item.genieName}</span>{" "}
                        <span className="text-gray-500">·</span>{" "}
                        <span className="text-gray-700">{item.reason}</span>
                      </p>
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        {formatRelativeDate(item.date)}
                        {item.durationSeconds !== null && (
                          <> · {formatDuration(item.durationSeconds)}</>
                        )}
                      </p>
                    </div>
                    {linkTarget && (
                      <ArrowRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                    )}
                  </div>
                );
                return (
                  <li key={`${item.id}-${item.reasonKind}-${idx}`}>
                    {linkTarget ? (
                      <Link to={linkTarget} className="block">
                        {Row}
                      </Link>
                    ) : (
                      Row
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};
